import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppShell, RoutePending, TabRouteSlot } from "@/components/app-shell/AppShell";
import { OnboardingScreen } from "@/features/onboarding/screens/OnboardingScreen";
import { WelcomeScreen } from "@/features/auth/screens/WelcomeScreen";
import { LoginScreen } from "@/features/auth/screens/LoginScreen";
import { SignUpScreen } from "@/features/auth/screens/SignUpScreen";
import { ForgotPasswordScreen } from "@/features/auth/screens/ForgotPasswordScreen";
import { ExperimentDetailScreen } from "@/features/experiments/screens/ExperimentDetailScreen";
import { ReflectScreen } from "@/features/experiments/screens/ReflectScreen";
import { PrivacyScreen } from "@/features/privacy/screens/PrivacyScreen";
import { ProgramsScreen } from "@/features/programs/screens/ProgramsScreen";
import { PaywallScreen } from "@/features/subscription/screens/PaywallScreen";
import { LearnScreen } from "@/features/learn/screens/LearnScreen";
import { JoinScreen } from "@/features/practice/screens/JoinScreen";
import { isAuthEnforced } from "@/lib/supabase/client";
import { useAuthStore } from "@/stores/auth-store";

async function ensureAuthBootstrapped() {
  const state = useAuthStore.getState();
  if (!state.bootstrapped) {
    await state.bootstrap();
  }
  // Profile must be loaded before onboarding guards (avoids false re-onboarding)
  const next = useAuthStore.getState();
  if (
    next.status === "authenticated" &&
    !next.profileHydrated
  ) {
    await next.refreshProfile();
  }
}

function guardAuthenticated() {
  if (!isAuthEnforced()) return;
  const { status } = useAuthStore.getState();
  if (status !== "authenticated") {
    throw redirect({ to: "/login" });
  }
}

function guardOnboardingIfNeeded() {
  if (!isAuthEnforced()) return;
  const { status, isOnboardingComplete } = useAuthStore.getState();
  if (status === "authenticated" && !isOnboardingComplete()) {
    throw redirect({ to: "/onboarding", search: { restart: false } });
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  beforeLoad: async () => {
    await ensureAuthBootstrapped();
  },
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/welcome",
  component: WelcomeScreen,
  beforeLoad: () => {
    if (!isAuthEnforced()) return;
    const { status, isOnboardingComplete } = useAuthStore.getState();
    if (status === "authenticated") {
      throw redirect(
        isOnboardingComplete()
          ? { to: "/app/today" }
          : { to: "/onboarding", search: { restart: false } }
      );
    }
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginScreen,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignUpScreen,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/forgot-password",
  component: ForgotPasswordScreen,
});

const joinWithTokenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join/$token",
  component: JoinScreen,
});

const joinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/join",
  component: JoinScreen,
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: OnboardingScreen,
  validateSearch: (search: Record<string, unknown>) => ({
    restart:
      search.restart === true ||
      search.restart === "1" ||
      search.restart === "true"
        ? true
        : false,
  }),
  beforeLoad: ({ search }) => {
    if (!isAuthEnforced()) return;
    const { status, isOnboardingComplete } = useAuthStore.getState();
    if (status !== "authenticated") {
      throw redirect({ to: "/signup" });
    }
    // Allow re-run from profile when ?restart=1
    if (isOnboardingComplete() && !search.restart) {
      throw redirect({ to: "/app/today" });
    }
  },
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AppShell,
  beforeLoad: () => {
    guardAuthenticated();
    guardOnboardingIfNeeded();
  },
});

const todayRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/today",
  component: TabRouteSlot,
});

const exploreRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/explore",
  component: TabRouteSlot,
});

const coachRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/coach",
  component: TabRouteSlot,
});

const insightsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/insights",
  component: TabRouteSlot,
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: TabRouteSlot,
});

const learnRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/learn",
  component: LearnScreen,
});

const privacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/privacy",
  component: PrivacyScreen,
});

const programsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/programs",
  component: ProgramsScreen,
});

const paywallRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/paywall",
  component: PaywallScreen,
  validateSearch: (
    search: Record<string, unknown>
  ): { reason?: string; checkout?: string } => {
    const out: { reason?: string; checkout?: string } = {};
    if (
      search.reason === "coach" ||
      search.reason === "experiment" ||
      search.reason === "programs" ||
      search.reason === "explore"
    ) {
      out.reason = search.reason;
    }
    if (search.checkout === "success" || search.checkout === "cancel") {
      out.checkout = search.checkout;
    }
    return out;
  },
});

const experimentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiment/$assignmentId",
  component: ExperimentDetailScreen,
  beforeLoad: () => {
    guardAuthenticated();
    guardOnboardingIfNeeded();
  },
});

const reflectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiment/$assignmentId/reflect",
  component: ReflectScreen,
  beforeLoad: () => {
    guardAuthenticated();
    guardOnboardingIfNeeded();
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    const { status, isOnboardingComplete } = useAuthStore.getState();
    if (!isAuthEnforced() || status === "demo") {
      throw redirect({ to: "/welcome" });
    }
    if (status === "authenticated") {
      throw redirect(
        isOnboardingComplete()
          ? { to: "/app/today" }
          : { to: "/onboarding", search: { restart: false } }
      );
    }
    throw redirect({ to: "/welcome" });
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  welcomeRoute,
  loginRoute,
  signUpRoute,
  forgotPasswordRoute,
  joinRoute,
  joinWithTokenRoute,
  onboardingRoute,
  experimentDetailRoute,
  reflectRoute,
  appRoute.addChildren([
    todayRoute,
    exploreRoute,
    coachRoute,
    insightsRoute,
    profileRoute,
    learnRoute,
    privacyRoute,
    programsRoute,
    paywallRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  defaultPendingComponent: RoutePending,
  // Tabs are keep-alive; avoid pending flash on quick switches
  defaultPendingMs: 400,
  defaultPendingMinMs: 180,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
